import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Heading, Input, VStack } from '@chakra-ui/react';

interface Options {
  label: string;
  value: string;
}

export default function TransferList({
  options,
  value,
  onChange,
}: {
  options: Options[];
  value: string[]; // array of `value`
  onChange: (updated: string[]) => void;
}) {
  const { t } = useTranslation();
  const selectedValues = value;

  // AVAILABLE = options where value NOT in selectedValues
  const available = useMemo(
    () =>
      options.filter((opt) => !selectedValues.includes(opt.value)),
    [options, selectedValues],
  );

  const selected = useMemo(
    () =>
      options.filter((opt) => selectedValues.includes(opt.value)),
    [options, selectedValues],
  );

  // local UI selections
  const [activeLeft, setActiveLeft] = useState<string[]>([]);
  const [activeRight, setActiveRight] = useState<string[]>([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  const filteredAvailable = available.filter((i) =>
    i.label.toLowerCase().includes(searchLeft.toLowerCase()),
  );

  const filteredSelected = selected.filter((i) =>
    i.label.toLowerCase().includes(searchRight.toLowerCase()),
  );

  const toggleLeft = (val: string) => {
    setActiveLeft((prev) =>
      prev.includes(val) ? prev.filter((i) => i !== val) : [...prev, val],
    );
  };

  const toggleRight = (val: string) => {
    setActiveRight((prev) =>
      prev.includes(val) ? prev.filter((i) => i !== val) : [...prev, val],
    );
  };

  const handleAdd = () => {
    if (activeLeft.length === 0) return;

    onChange([...selectedValues, ...activeLeft]); // add values
    setActiveLeft([]);
  };

  const handleRemove = () => {
    if (activeRight.length === 0) return;

    onChange(selectedValues.filter((val) => !activeRight.includes(val))); // remove values
    setActiveRight([]);
  };

  return (
    <Flex gap="40px" align="flex-start" width="100%" data-e2e="transfer-list">
      {/* LEFT LIST */}
      <Box width="100%">
        <Heading as="h3" size="sm" mb={2}>
          {t('forms.availablePermissions', 'Available Permissions')}
        </Heading>

        <Input
          aria-label={t('forms.searchAvailablePermissions', 'Search available permissions')}
          size="sm"
          mb={2}
          placeholder={t('common.searchPlaceholder', 'Search...')}
          value={searchLeft}
          onChange={(e) => setSearchLeft(e.target.value)}
          data-e2e="search-left"
        />

        <Box
          border="1px solid"
          borderColor="border"
          borderRadius="md"
          p={3}
          h="300px"
          overflowY="auto"
        >
          <VStack align="stretch">
            {filteredAvailable.map((item) => (
              <Box
                key={item.value}
                p={2}
                borderRadius="md"
                bg={
                  activeLeft.includes(item.value) ? 'blue.100' : 'transparent'
                }
                cursor="pointer"
                onClick={() => toggleLeft(item.value)}
                data-e2e={`available-item-${item.value}`}
              >
                {item.label}
              </Box>
            ))}
          </VStack>
        </Box>
      </Box>

      {/* BUTTONS */}
      <Flex direction="column" gap={3} mt="60px">
        <Button
          aria-label={t('forms.addSelectedPermissions', 'Add selected permissions')}
          colorScheme="blue"
          onClick={handleAdd}
          disabled={!activeLeft.length}
          type="button"
          data-e2e="move-right-button"
        >
          ➜
        </Button>
        <Button
          aria-label={t('forms.removeSelectedPermissions', 'Remove selected permissions')}
          colorScheme="red"
          onClick={handleRemove}
          disabled={!activeRight.length}
          type="button"
          data-e2e="move-left-button"
        >
          ←
        </Button>
      </Flex>

      {/* RIGHT LIST */}
      <Box width="100%">
        <Heading as="h3" size="sm" mb={2}>
          {t('forms.selectedPermissions', 'Selected Permissions')}
        </Heading>

        <Input
          aria-label={t('forms.searchSelectedPermissions', 'Search selected permissions')}
          size="sm"
          mb={2}
          placeholder={t('common.searchPlaceholder', 'Search...')}
          value={searchRight}
          onChange={(e) => setSearchRight(e.target.value)}
          data-e2e="search-right"
        />

        <Box
          border="1px solid"
          borderColor="border"
          borderRadius="md"
          p={3}
          h="300px"
          overflowY="auto"
        >
          <VStack align="stretch">
            {filteredSelected?.map((item) => (
              <Box
                key={item.value}
                p={2}
                borderRadius="md"
                bg={
                  activeRight.includes(item.value) ? 'red.100' : 'transparent'
                }
                cursor="pointer"
                onClick={() => toggleRight(item.value)}
                data-e2e={`selected-item-${item.value}`}
              >
                {item.label}
              </Box>
            ))}
          </VStack>
        </Box>
      </Box>
    </Flex>
  );
}
